import React, { useState, useContext, useEffect, useRef } from "react";
import { useAnagramsContext } from "./anagram-provider";

interface IScoreSet {
  bonusScore: number;
  totalScore: number;
  wordScore: number;
}

interface IScoreContext {
  scores: IScoreSet;
  setScores?: (scores: IScoreSet) => void;
}

const defaultState: IScoreContext = {
  scores: { bonusScore: 0, totalScore: 0, wordScore: 0 },
};

const ScoresContext = React.createContext<IScoreContext>(defaultState);

const WORD_POINTS_PER_LETTER = 5;
const BONUS_POINTS_PER_LETTER = 2;

// words in l1 that aren't accounted for in l2
function listDiff(l1: string[], l2: string[]) {
  const diff = [...l1];
  l2.forEach((word) => {
    const indexOfWord = diff.indexOf(word);
    if (indexOfWord > -1) {
      diff.splice(indexOfWord, 1);
    }
  });
  return diff;
}

function pointsFor(words: string[], pointsPerLetter: number) {
  return words.reduce((sum, word) => sum + word.length * pointsPerLetter, 0);
}

function loadScores(): IScoreSet {
  const stored = window.localStorage.getItem("scores");
  if (stored) {
    return JSON.parse(stored);
  }
  return { bonusScore: 0, totalScore: 0, wordScore: 0 };
}

export function ScoresProvider(props: { children: any }) {
  const anagramsContext = useAnagramsContext();

  const [scores, setScores] = useState<IScoreSet>(loadScores);

  // The word lists that have already been scored. Only words that appear
  // after this snapshot are new, so every word is scored exactly once.
  const scoredRef = useRef({
    foundWords: anagramsContext.foundWords,
    bonusWords: anagramsContext.bonusWords,
  });

  useEffect(() => {
    const newWords = listDiff(
      anagramsContext.foundWords,
      scoredRef.current.foundWords
    );
    const newBonusWords = listDiff(
      anagramsContext.bonusWords,
      scoredRef.current.bonusWords
    );
    scoredRef.current = {
      foundWords: anagramsContext.foundWords,
      bonusWords: anagramsContext.bonusWords,
    };

    if (newWords.length === 0 && newBonusWords.length === 0) {
      return;
    }

    // functional update so concurrent score changes can't overwrite each other
    setScores((current) => {
      const wordScore = pointsFor(newWords, WORD_POINTS_PER_LETTER);
      const bonusScore = pointsFor(newBonusWords, BONUS_POINTS_PER_LETTER);
      return {
        wordScore: current.wordScore + wordScore,
        bonusScore: current.bonusScore + bonusScore,
        totalScore: current.totalScore + wordScore + bonusScore,
      };
    });
  }, [anagramsContext.foundWords, anagramsContext.bonusWords]);

  useEffect(() => {
    window.localStorage.setItem("scores", JSON.stringify(scores));
  }, [scores]);

  const updateScores = (next: IScoreSet) => {
    setScores(next);
  };

  return (
    <ScoresContext.Provider value={{ scores, setScores: updateScores }}>
      {props.children}
    </ScoresContext.Provider>
  );
}

export const useScoreContext = () => useContext(ScoresContext);
